import React, { createRef, useEffect, useRef, useState } from 'react';
import { BedRoomBox } from './BedRoomBox';
import { IconArrowLeftThree, IconArrowRightThree, IconBed, IconX } from '../../assets/icons';
import { TranslationUtils } from '../../utils';
import { useSelector } from 'react-redux';
import { TripState } from '../../types/trip.store.type';
import { RoomsDetail } from 'guest-data-types-ts-lib/types';
import './BedRoomLayuot.scss';

export const BedRoomLayout: React.FC = () => {
    const { home_info } = useSelector((store: { trip: TripState }) => store.trip);
    const carouselRef = useRef<HTMLDivElement>(null);
    const bedRoomContainerRef = useRef<HTMLDivElement>(null);
    const popupContentRef = useRef<HTMLDivElement>(null);
    const [leftArrowVisible, setLeftArrowVisible] = useState(false);
    const [rightArrowVisible, setRightArrowVisible] = useState(true);
    const [isPopupVisible, setPopupVisible] = useState<boolean>(false);
    const [numberOfBedOnScreen, setNumberOfBedOnScreen] = useState<number>(4);
    const [boxRefs, setBoxRefs] = useState<React.RefObject<HTMLDivElement>[]>([]);
    const [gridRefs, setGridRefs] = useState<React.RefObject<HTMLDivElement>[]>([]);
    const [_, setCurrentIndex] = useState(0);

    const rooms = home_info?.bedrooms_details || [];
    const numberOfRooms = rooms.length;
    const moreThanFourBeds = numberOfRooms > numberOfBedOnScreen;

    useEffect(() => {
        setBoxRefs((boxRefs) =>
            Array(numberOfRooms)
                .fill(0)
                .map((_, i) => boxRefs[i] || createRef<HTMLDivElement>())
        );
        setGridRefs((boxRefs) =>
            Array(numberOfRooms)
                .fill(0)
                .map((_, i) => gridRefs[i] || createRef<HTMLDivElement>())
        );
    }, [numberOfRooms]);

    useEffect(() => {
        const handleScreenSizes = () => {
            if (carouselRef.current) {
                carouselRef.current.scrollLeft = 0;
                setCurrentIndex(0);
                setLeftArrowVisible(false);
                setRightArrowVisible(true);
            }
            if (window.innerWidth < 1200) {
                setNumberOfBedOnScreen(3);
            }
            if (window.innerWidth < 768) {
                setNumberOfBedOnScreen(2);
            }
        };
        handleScreenSizes();
        window.addEventListener('resize', handleScreenSizes);
        return () => window.removeEventListener('resize', handleScreenSizes);
    }, []);

    useEffect(() => {
        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [isPopupVisible]);

    const handleDocumentClick = (event: any) => {
        if (isPopupVisible && popupContentRef.current && !popupContentRef.current.contains(event.target)) {
            setPopupVisible(false);
        }
    };

    const scrollToRight = () => {
        setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex < boxRefs.length - 1 ? prevIndex + 1 : prevIndex;
            boxRefs[newIndex].current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current!;
            const sizeOfOneBox = Math.ceil(bedRoomContainerRef.current!.offsetWidth / numberOfRooms);
            setRightArrowVisible(Math.ceil(scrollLeft + sizeOfOneBox * 2) < scrollWidth - clientWidth);
            setLeftArrowVisible(Math.ceil(scrollLeft + sizeOfOneBox) > 0);
            return newIndex;
        });
    };

    const scrollToLeft = () => {
        setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex > 0 ? prevIndex - 1 : prevIndex;
            boxRefs[newIndex].current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current!;
            const sizeOfOneBox = Math.ceil(bedRoomContainerRef.current!.offsetWidth / numberOfRooms);
            setRightArrowVisible(Math.ceil(scrollLeft - sizeOfOneBox) < scrollWidth - clientWidth);
            setLeftArrowVisible(Math.ceil(scrollLeft - sizeOfOneBox) > 1);
            return newIndex;
        });
    };

    return (
        <div id={'bed-room-layout'} className={'bed-room-layout'}>
            <div className={'d-flex flex-row align-items-center'}>
                <IconBed className={'essential-icon'} />
                <p className={'body-small-bold m-0'}>{TranslationUtils.get('bedroom_title')}</p>
            </div>
            <div id={'carousel-container'} className={'carousel-container mb-4'}>
                {moreThanFourBeds && leftArrowVisible && (
                    <button
                        id={'carousel-btn-left'}
                        className={`carousel-btn carousel-btn-left ${isPopupVisible ? 'carousel-btn-hidden' : ''}`}
                        onClick={scrollToLeft}
                    >
                        <IconArrowLeftThree />
                    </button>
                )}
                <div id={'carousel'} className={'carousel h-100'} ref={carouselRef}>
                    <div id={'carousel-content'} className={'d-flex'}>
                        <div id={'bed-room-container'} className={'bed-room-container d-flex flex-nowrap h-100'} ref={bedRoomContainerRef}>
                            {rooms.map((room: RoomsDetail, index: number) => (
                                <BedRoomBox key={index} {...room} boxRef={boxRefs[index]} />
                            ))}
                        </div>
                    </div>
                </div>
                {moreThanFourBeds && rightArrowVisible && (
                    <button
                        id={'carousel-btn-right'}
                        className={`carousel-btn carousel-btn-right ${isPopupVisible ? 'carousel-btn-hidden' : ''}`}
                        onClick={scrollToRight}
                    >
                        <IconArrowRightThree />
                    </button>
                )}
                {moreThanFourBeds && (
                    <button
                        id={'popup-trigger'}
                        data-testid={'popup-trigger'}
                        className={'popup-btn mt-2'}
                        onClick={() => setPopupVisible(true)}
                    >
                        {TranslationUtils.get('view_all_beds', { total: rooms.length })}
                    </button>
                )}
                {isPopupVisible && (
                    <div id={'popup-container'} className={'popup-container'} data-testid="popup-container">
                        <div id={'popup-content'} className={'popup-content'} ref={popupContentRef}>
                            <div id={'popup-header'} className={'popup-header w-100'}>
                                <p id={'page-subtitle'} className={'page-subtitle'}>
                                    {TranslationUtils.get('bedroom_title')}
                                </p>
                                <button id={'popup-close-btn'} data-testid={'popup-close-btn'} onClick={() => setPopupVisible(false)}>
                                    <IconX />
                                </button>
                            </div>
                            <div id={'rooms-grid'} className={'rooms-grid'}>
                                {rooms.map((room: RoomsDetail, index: number) => (
                                    <BedRoomBox key={index} {...room} boxRef={gridRefs[index]} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
